import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { CaretDown, Link as LinkIcon, Trash, MagnifyingGlass } from '@phosphor-icons/react';

const TYPE_META = {
  offerte:    { kort: 'OFF', label: 'Offerte',    kleur: '#7C3AED', bg: '#EDE9FE' },
  proforma:   { kort: 'PRO', label: 'Proforma',   kleur: '#B45309', bg: '#FEF3C7' },
  bestelbon:  { kort: 'BB',  label: 'Bestelbon',  kleur: '#0E7490', bg: '#CFFAFE' },
  leverbon:   { kort: 'LEV', label: 'Leverbon',   kleur: '#059669', bg: '#D1FAE5' },
  factuur:    { kort: 'FAC', label: 'Factuur',    kleur: '#002FA7', bg: '#DBEAFE' },
  creditnota: { kort: 'CN',  label: 'Creditnota', kleur: '#DC2626', bg: '#FEE2E2' },
};

const STATUS_KLEUREN = {
  'betaald': '#16A34A', 'goedgekeurd': '#16A34A', 'geleverd': '#16A34A', 'besteld': '#16A34A',
  'verzonden': '#0284C7', 'openstaand': '#F59E0B', 'gedeeltelijk betaald': '#F59E0B',
  'vervallen': '#DC2626', 'afgewezen': '#DC2626', 'concept': '#6B7280',
};

export default function DocumentenStroom({ docId, klantId, onChange, refreshKey = 0 }) {
  const navigate = useNavigate();
  const [keten, setKeten] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toonKoppel, setToonKoppel] = useState(false);
  const [kandidaten, setKandidaten] = useState([]);
  const [zoek, setZoek] = useState('');

  const load = () => {
    if (!docId) return;
    API.get(`/documenten/${docId}/stroom`).then(r => setKeten(r.data)).catch(() => setKeten(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [docId, refreshKey]);

  const openKoppel = async () => {
    setToonKoppel(v => !v);
    if (!kandidaten.length && klantId) {
      try {
        const r = await API.get('/documenten', { params: { klant_id: klantId } });
        setKandidaten((r.data || []).filter(d => d.id !== docId));
      } catch { /* ignore */ }
    }
  };

  const koppel = async (bronId) => {
    setBusy(true);
    try {
      await API.post(`/documenten/${docId}/koppel-bron`, { bron_id: bronId });
      setToonKoppel(false);
      load();
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const ontkoppel = async () => {
    if (!window.confirm('Document loskoppelen van de stroom?')) return;
    setBusy(true);
    try {
      await API.post(`/documenten/${docId}/koppel-bron`, { bron_id: null });
      load();
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const chain = keten?.keten || [];
  const isOrphan = chain.length <= 1;

  // Groepeer per type voor mooiere weergave
  const filteredKandidaten = kandidaten.filter(k =>
    !zoek || k.id.toLowerCase().includes(zoek.toLowerCase()) || (k.ref || '').toLowerCase().includes(zoek.toLowerCase())
  );

  return (
    <div className="bg-white border border-gray-200" data-testid="docstroom">
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-400">Documentenstroom</span>
        {!isOrphan && (
          <button className="text-[9px] text-gray-400 hover:text-red-600" onClick={ontkoppel} disabled={busy} title="Loskoppelen" data-testid="docstroom-unlink">
            <Trash size={10} />
          </button>
        )}
      </div>
      <div className="px-3 py-2">
        {chain.length === 0 && <div className="text-center text-gray-300 text-[10px] py-2">Laden…</div>}
        {chain.map((d, i) => {
          const m = TYPE_META[d.type] || TYPE_META.factuur;
          const isCurrent = d.id === docId;
          return (
            <div key={d.id} className="relative">
              {i > 0 && <div className="ml-[14px] border-l-2 border-dashed border-gray-200 h-2" />}
              <button
                type="button"
                onClick={() => !isCurrent && navigate(`/documenten/${d.id}/bewerk`)}
                disabled={isCurrent}
                className={`w-full flex items-start gap-2 p-1.5 rounded transition text-left ${isCurrent ? 'bg-blue-50 border border-blue-200 cursor-default' : 'hover:bg-gray-50 cursor-pointer'}`}
                data-testid={`docstroom-item-${d.id}`}
              >
                <span className="shrink-0 w-8 h-8 rounded flex items-center justify-center text-[10px] font-bold" style={{ background: m.bg, color: m.kleur }}>{m.kort}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className={`font-bold text-[11px] font-mono truncate ${isCurrent ? 'text-gray-900' : 'text-[#002FA7]'}`}>{d.id}</span>
                    {isCurrent && <span className="text-[8px] uppercase tracking-wider font-bold text-blue-700 bg-blue-100 px-1 rounded">nu</span>}
                  </div>
                  <div className="flex items-center gap-1 text-[9px] text-gray-500">
                    <span>{d.datum}</span>
                    <span>·</span>
                    <span className="font-bold" style={{ color: STATUS_KLEUREN[d.status] || '#6B7280' }}>{d.status}</span>
                    {d.handtekening_datum && <span className="ml-0.5" title="Ondertekend">✍</span>}
                  </div>
                  {d.totaal > 0 && <div className="text-[9px] text-gray-600 font-mono">{fmt(d.totaal)}</div>}
                </div>
              </button>
            </div>
          );
        })}

        {isOrphan && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={openKoppel}
              className="w-full text-[10px] text-gray-500 hover:text-[#002FA7] inline-flex items-center justify-center gap-1 py-1"
              data-testid="docstroom-koppel-open"
            >
              <LinkIcon size={11} /> Koppel aan bestaand document
              <CaretDown size={10} className={`transition ${toonKoppel ? 'rotate-180' : ''}`} />
            </button>
            {toonKoppel && (
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <div className="relative mb-2">
                  <MagnifyingGlass size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    className="w-full text-[10px] rounded border border-gray-200 py-1"
                    style={{ paddingLeft: 22 }}
                    placeholder="Zoek op nummer of ref"
                    value={zoek}
                    onChange={e => setZoek(e.target.value)}
                    data-testid="docstroom-koppel-zoek"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredKandidaten.slice(0, 30).map(k => {
                    const m = TYPE_META[k.type] || TYPE_META.factuur;
                    return (
                      <button
                        key={k.id}
                        type="button"
                        onClick={() => koppel(k.id)}
                        className="w-full flex items-center gap-2 p-1.5 rounded bg-white hover:bg-[#F0F4FF] text-left border border-transparent hover:border-[#002FA7]"
                        data-testid={`docstroom-koppel-kandidaat-${k.id}`}
                      >
                        <span className="shrink-0 w-6 h-6 rounded flex items-center justify-center text-[8px] font-bold" style={{ background: m.bg, color: m.kleur }}>{m.kort}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-mono font-bold truncate text-[#002FA7]">{k.id}</div>
                          <div className="text-[9px] text-gray-400 truncate">{k.datum} · {k.status}</div>
                        </div>
                        <span className="text-[9px] font-mono text-gray-600">{fmt(k.totaal || 0)}</span>
                      </button>
                    );
                  })}
                  {kandidaten.length === 0 && <div className="text-center text-[10px] text-gray-400 py-4">Geen andere documenten voor deze klant</div>}
                  {filteredKandidaten.length === 0 && kandidaten.length > 0 && <div className="text-center text-[10px] text-gray-400 py-2">Geen resultaten voor "{zoek}"</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
